import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertCircle,
  Building2,
  CheckCircle2,
  ChevronRight,
  CloudDownload,
  Copy,
  Download,
  ExternalLink,
  FileArchive,
  FileText,
  Gauge,
  Hash,
  Headphones,
  HelpCircle,
  Image as ImageIcon,
  Info,
  KeyRound,
  Layers,
  Link2,
  Loader2,
  Lock,
  MonitorSmartphone,
  MoveLeft,
  PackageCheck,
  ShieldCheck,
  WifiOff,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { formatDate } from "@/lib/format";

const fmtSize = (b) =>
  !b ? "" : b >= 1e9 ? `${(b / 1e9).toFixed(2)} Go` : b >= 1e6 ? `${(b / 1e6).toFixed(2)} Mo` : `${Math.max(1, Math.round(b / 1e3))} Ko`;

const fmtValidated = (value) => {
  if (!value) return "";
  try {
    const d = new Date(value);
    const date = new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "long", year: "numeric" }).format(d);
    const time = new Intl.DateTimeFormat("fr-FR", { hour: "2-digit", minute: "2-digit" }).format(d);
    return `${date} à ${time}`;
  } catch (e) {
    return "";
  }
};

const FILE_TYPES = {
  zip: "Archive ZIP", rar: "Archive RAR", "7z": "Archive 7-Zip", pdf: "Document PDF", txt: "Fichier texte",
  doc: "Document Word", docx: "Document Word", xls: "Classeur Excel", xlsx: "Classeur Excel", csv: "Fichier CSV",
  ppt: "Présentation PowerPoint", pptx: "Présentation PowerPoint", png: "Image PNG", jpg: "Image JPEG",
  jpeg: "Image JPEG", webp: "Image WebP", gif: "Image GIF",
};

const fileTypeLabel = (item) => {
  if (item.type === "link") return "Lien externe";
  if (item.type === "key") return "Clé de licence";
  if (item.type === "text") return "Instructions";
  const ext = (item.file_name || "").includes(".") ? item.file_name.split(".").pop().toLowerCase() : "";
  return FILE_TYPES[ext] || (ext ? `Fichier .${ext.toUpperCase()}` : "Fichier");
};

const ITEM_ICONS = { link: Link2, key: KeyRound, text: FileText, file: FileArchive, image: ImageIcon };

function Crumb({ to, children }) {
  return (
    <>
      <Link to={to} className="shrink-0 text-slate-500 transition-colors duration-200 hover:text-brand">
        {children}
      </Link>
      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-300" />
    </>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <p className="flex items-start gap-2 text-sm">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
      <span className="text-slate-500">{label} :</span>
      <span className="min-w-0 break-words font-bold text-ink">{value}</span>
    </p>
  );
}

function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-3 border-b border-line py-2 text-sm last:border-0">
      <span className="text-slate-500">{label}</span>
      <span className="min-w-0 break-words text-right font-bold text-ink">{value}</span>
    </div>
  );
}

export default function AccountDeliveryLaunch() {
  const { id, itemId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/my/delivery/${id}`);
      setData(res);
    } catch (err) {
      setError(apiError(err, "Commande introuvable"));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copié !");
    } catch (e) {
      toast.error("Copie impossible — sélectionnez le texte manuellement");
    }
  };

  const item = data?.items?.find((i) => i.id === itemId);

  const download = async () => {
    setDownloading(true);
    try {
      const { data: blob } = await api.get(`/my/delivery/${id}/file/${itemId}`, {
        responseType: "blob",
        params: { download: 1 },
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.file_name || item.title;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Téléchargement lancé");
    } catch (err) {
      toast.error(apiError(err, "Téléchargement impossible"));
    } finally {
      setDownloading(false);
      load();
    }
  };

  if (error) {
    return (
      <div className="container-it py-16">
        <div className="mx-auto max-w-lg rounded-2xl border border-line bg-white p-10 text-center">
          <p className="text-sm font-bold text-ink">{error}</p>
          <Link to="/compte/commandes" className="mt-4 inline-block rounded-full bg-brand px-5 py-2.5 text-xs font-bold text-white">
            Retour à mes commandes
          </Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex items-center justify-center py-32 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const product = data.product || {};
  const backUrl = `/compte/commandes/${id}/telechargement`;
  const breadcrumb = (
    <nav className="flex flex-wrap items-center gap-1.5 text-xs font-semibold" data-testid="launch-breadcrumb">
      <Crumb to="/">Accueil</Crumb>
      <Crumb to="/compte">Mon compte</Crumb>
      <Crumb to="/compte/commandes">Mes commandes</Crumb>
      <Crumb to={`/compte/commandes/${id}`}>Commande #{data.order_number}</Crumb>
      <Crumb to={backUrl}>Téléchargements</Crumb>
      <span className="shrink-0 font-bold text-ink">Lancer le téléchargement</span>
    </nav>
  );

  if (data.locked) {
    return (
      <div className="container-it space-y-5 py-6 lg:py-8" data-testid="launch-page">
        {breadcrumb}
        <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 sm:items-center" data-testid="launch-locked-banner">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
            <Lock className="h-5 w-5 text-amber-600" />
          </span>
          <div>
            <p className="text-sm font-extrabold text-amber-800">Paiement en attente de confirmation</p>
            <p className="text-xs leading-relaxed text-amber-700">
              Le téléchargement se débloque automatiquement dès la confirmation de votre paiement.
            </p>
          </div>
        </div>
        <div className="rounded-2xl border border-line bg-white p-8 text-center sm:p-10" data-testid="launch-locked-panel">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
            <Lock className="h-6 w-6 text-slate-400" />
          </span>
          <p className="mt-4 text-sm font-bold text-ink">Accès verrouillé</p>
          <p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-slate-500">
            Même si vous connaissez l'adresse de cette page, l'accès reste bloqué côté serveur tant que la commande n'est pas validée.
          </p>
          <button
            type="button"
            disabled
            data-testid="launch-locked-download-btn"
            className="mx-auto mt-5 flex cursor-not-allowed items-center gap-2 rounded-full bg-slate-200 px-6 py-3 text-xs font-bold text-slate-400"
          >
            <Lock className="h-3.5 w-3.5" /> Télécharger
          </button>
        </div>
        <div className="text-center">
          <Link to={backUrl} data-testid="launch-back-btn" className="inline-flex items-center gap-2 text-sm font-bold text-brand hover:underline">
            <MoveLeft className="h-4 w-4" /> Retour à mes téléchargements
          </Link>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="container-it space-y-5 py-6 lg:py-8" data-testid="launch-page">
        {breadcrumb}
        <div className="rounded-2xl border border-line bg-white p-10 text-center">
          <p className="text-sm font-bold text-ink">Contenu introuvable</p>
          <p className="mt-1 text-xs text-slate-500">Ce contenu n'existe pas ou n'est plus disponible pour cette commande.</p>
          <Link to={backUrl} className="mt-4 inline-block rounded-full bg-brand px-5 py-2.5 text-xs font-bold text-white">
            Retour à mes téléchargements
          </Link>
        </div>
      </div>
    );
  }

  const ItemIcon = ITEM_ICONS[item.type] || FileArchive;
  const exhausted = item.download_limit > 0 && item.downloads_used >= item.download_limit;
  const blocked = item.is_expired || exhausted;
  const remaining = item.download_limit > 0 ? Math.max(0, item.download_limit - item.downloads_used) : null;
  const isFile = item.type === "file" || item.type === "image";

  const statusBadge = item.is_expired ? (
    <span className="flex items-center gap-1.5 text-[11px] font-bold text-rose-600"><Lock className="h-3.5 w-3.5" /> Expiré</span>
  ) : exhausted ? (
    <span className="flex items-center gap-1.5 text-[11px] font-bold text-amber-600"><Lock className="h-3.5 w-3.5" /> Limite atteinte</span>
  ) : (
    <span className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> {isFile ? "Prêt à télécharger" : "Disponible"}</span>
  );

  return (
    <div className="container-it space-y-5 py-6 lg:py-8" data-testid="launch-page">
      {breadcrumb}

      {/* ---- Bandeau de confirmation ---- */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="launch-confirm-banner">
        <div className="flex items-start gap-3 sm:items-center">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          </span>
          <div>
            <p className="text-sm font-extrabold text-emerald-800">Commande confirmée et validée</p>
            <p className="text-xs leading-relaxed text-emerald-700">
              Votre commande a été validée par l'administrateur. Vous pouvez maintenant télécharger vos fichiers. Merci pour votre achat !
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="rounded-full border border-emerald-200 bg-emerald-100 px-3 py-1 text-[11px] font-bold text-emerald-700">Validée</span>
          {data.validated_at && <span className="text-[11px] font-semibold text-emerald-700">le {fmtValidated(data.validated_at)}</span>}
        </div>
      </div>

      {/* ---- Carte produit ---- */}
      <div className="rounded-2xl border border-line bg-white p-4 sm:p-6" data-testid="launch-product-card">
        <div className="grid gap-6 lg:grid-cols-[auto_1fr_280px]">
          <div className="mx-auto w-36 shrink-0 sm:w-40 lg:mx-0">
            {product.image_url ? (
              <img src={product.image_url} alt={product.name} className="w-full rounded-xl border border-line object-cover shadow-sm" />
            ) : (
              <div className="flex h-48 w-full items-center justify-center rounded-xl bg-slate-100">
                <PackageCheck className="h-8 w-8 text-slate-300" />
              </div>
            )}
          </div>
          <div className="min-w-0">
            <h1 className="font-display text-xl font-extrabold text-ink sm:text-2xl" data-testid="launch-product-title">
              {product.name}
            </h1>
            <div className="mt-4 space-y-2.5">
              <InfoRow icon={Building2} label="Éditeur" value={product.editor} />
              <InfoRow icon={MonitorSmartphone} label="Plateforme" value={product.platform} />
              <InfoRow icon={Layers} label="Catégorie" value={product.category} />
              <InfoRow icon={ShieldCheck} label="Licence" value={product.license_type} />
              {data.total_size > 0 && <InfoRow icon={FileArchive} label="Taille totale" value={fmtSize(data.total_size)} />}
              {data.files_count > 0 && <InfoRow icon={FileText} label="Nombre de fichiers" value={String(data.files_count)} />}
              <InfoRow icon={CheckCircle2} label="Date d'achat" value={formatDate(data.created_at)} />
              <InfoRow icon={Hash} label="Commande" value={`#${data.order_number}`} />
            </div>
          </div>
          <div className="h-fit rounded-2xl border border-line bg-slate-50/60 p-4">
            <div className="flex items-center justify-between gap-2">
              <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
                <PackageCheck className="h-4 w-4 text-slate-400" /> Statut
              </p>
              <span className="rounded-full border border-emerald-200 bg-emerald-100 px-3 py-1 text-[11px] font-bold text-emerald-700">Validée</span>
            </div>
            {data.validated_at && (
              <p className="mt-3 text-xs text-slate-500">
                Achat validé le
                <span className="mt-0.5 block text-sm font-bold text-ink">{fmtValidated(data.validated_at)}</span>
              </p>
            )}
            <Link
              to={`/compte/commandes/${id}`}
              data-testid="launch-view-order-btn"
              className="mt-4 flex items-center justify-center rounded-full border border-brand/30 px-4 py-2.5 text-xs font-bold text-brand transition-colors duration-200 hover:bg-brand hover:text-white"
            >
              Voir le détail de la commande
            </Link>
          </div>
        </div>
      </div>

      {/* ---- Fichier sélectionné ---- */}
      <div className="rounded-2xl border border-line bg-white p-4 sm:p-6" data-testid="launch-selected-file">
        <h2 className="font-display text-base font-extrabold text-ink">
          {isFile ? "Fichier sélectionné" : "Contenu sélectionné"}
        </h2>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line p-4">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-light">
              <ItemIcon className="h-5 w-5 text-brand" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-ink">{item.title}</p>
              <p className="truncate text-xs text-slate-500">
                {isFile ? `${item.file_name}${item.file_size ? ` · ${fmtSize(item.file_size)}` : ""}` : fileTypeLabel(item)}
              </p>
              {item.description && <p className="truncate text-[11px] text-slate-400">{item.description}</p>}
            </div>
          </div>
          {statusBadge}
        </div>
      </div>

      {/* ---- Grande zone de lancement ---- */}
      <div className="rounded-2xl border border-line bg-white p-6 text-center sm:p-10" data-testid="launch-zone">
        {blocked ? (
          <>
            <span className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${item.is_expired ? "bg-rose-50" : "bg-amber-50"}`}>
              <Lock className={`h-7 w-7 ${item.is_expired ? "text-rose-500" : "text-amber-500"}`} />
            </span>
            <h2 className="mt-4 font-display text-lg font-extrabold text-ink sm:text-xl">
              {item.is_expired ? "Ce contenu a expiré" : "Limite de téléchargements atteinte"}
            </h2>
            <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
              {item.is_expired
                ? "La période d'accès à ce contenu est terminée. Contactez le support si vous avez besoin d'un nouvel accès."
                : "Vous avez utilisé tous vos téléchargements autorisés pour ce fichier. Contactez le support si besoin."}
            </p>
            <Link
              to="/support"
              className="mx-auto mt-5 inline-flex items-center gap-2 rounded-full border border-brand/30 px-6 py-3 text-xs font-bold text-brand transition-colors duration-200 hover:bg-brand hover:text-white"
            >
              <Headphones className="h-3.5 w-3.5" /> Contacter le support
            </Link>
          </>
        ) : isFile ? (
          <>
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-light">
              <CloudDownload className="h-7 w-7 text-brand" />
            </span>
            <h2 className="mt-4 font-display text-lg font-extrabold text-ink sm:text-xl">Prêt à lancer votre téléchargement</h2>
            <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
              Votre téléchargement va commencer. Cliquez sur le bouton ci-dessous pour démarrer.
            </p>
            <button
              type="button"
              data-testid="launch-download-btn"
              onClick={download}
              disabled={downloading}
              className="mx-auto mt-6 flex w-full max-w-md items-center justify-center gap-2.5 rounded-full bg-brand px-8 py-4 text-sm font-black uppercase tracking-wide text-white transition-colors duration-200 hover:bg-brand/90 disabled:opacity-60"
            >
              {downloading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />}
              {downloading ? "Téléchargement en cours…" : "Lancer le téléchargement"}
            </button>
            <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] font-semibold text-slate-400">
              <ShieldCheck className="h-3.5 w-3.5" /> Téléchargement 100% sécurisé
            </p>
          </>
        ) : item.type === "link" ? (
          <>
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-light">
              <Link2 className="h-7 w-7 text-brand" />
            </span>
            <h2 className="mt-4 font-display text-lg font-extrabold text-ink sm:text-xl">Votre lien d'accès est prêt</h2>
            <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">Cliquez sur le bouton ci-dessous pour accéder au contenu.</p>
            <div className="mx-auto mt-6 flex w-full max-w-md flex-col gap-2 sm:flex-row">
              <a
                href={item.url}
                target="_blank"
                rel="noreferrer"
                data-testid="launch-open-link-btn"
                className="flex flex-1 items-center justify-center gap-2.5 rounded-full bg-brand px-8 py-4 text-sm font-black uppercase tracking-wide text-white transition-colors duration-200 hover:bg-brand/90"
              >
                <ExternalLink className="h-5 w-5" /> Accéder au lien
              </a>
              <button
                type="button"
                data-testid="launch-copy-link-btn"
                onClick={() => copy(item.url)}
                className="flex items-center justify-center gap-2 rounded-full border border-line px-6 py-4 text-xs font-bold text-ink transition-colors duration-200 hover:border-brand hover:text-brand"
              >
                <Copy className="h-4 w-4" /> Copier
              </button>
            </div>
          </>
        ) : item.type === "key" ? (
          <>
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-light">
              <KeyRound className="h-7 w-7 text-brand" />
            </span>
            <h2 className="mt-4 font-display text-lg font-extrabold text-ink sm:text-xl">Votre clé de licence</h2>
            <code className="mx-auto mt-5 block w-full max-w-md rounded-xl border border-line bg-slate-50 px-4 py-4 font-mono text-base font-black tracking-widest text-ink" data-testid="launch-key-value">
              {item.value}
            </code>
            <button
              type="button"
              data-testid="launch-copy-key-btn"
              onClick={() => copy(item.value)}
              className="mx-auto mt-4 flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-xs font-bold text-white transition-colors duration-200 hover:bg-brand/90"
            >
              <Copy className="h-4 w-4" /> Copier la clé
            </button>
          </>
        ) : (
          <div className="mx-auto max-w-xl whitespace-pre-wrap rounded-xl border border-line bg-slate-50 p-4 text-left text-sm leading-relaxed text-ink">
            {item.content}
          </div>
        )}
      </div>

      {/* ---- Important ---- */}
      <div className="flex items-start gap-3 rounded-2xl border border-brand/20 bg-brand-light/60 p-4" data-testid="launch-important-card">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white">
          <Info className="h-5 w-5 text-brand" />
        </span>
        <div>
          <p className="text-sm font-extrabold text-ink">Important</p>
          <p className="mt-0.5 whitespace-pre-wrap text-xs leading-relaxed text-slate-600">{data.notice}</p>
        </div>
      </div>

      {/* ---- Détails + Aide ---- */}
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-2xl border border-line bg-white p-4 sm:p-6" data-testid="launch-file-details">
          <h2 className="font-display text-base font-extrabold text-ink">{isFile ? "Détails du fichier" : "Détails du contenu"}</h2>
          <div className="mt-3">
            <DetailRow label={isFile ? "Nom du fichier" : "Nom"} value={isFile ? item.file_name : item.title} />
            {isFile && item.file_size > 0 && <DetailRow label="Taille" value={fmtSize(item.file_size)} />}
            <DetailRow label="Type" value={fileTypeLabel(item)} />
            <DetailRow label="Disponible depuis" value={fmtValidated(data.validated_at)} />
            <DetailRow
              label="Expire le"
              value={item.expires_at ? `${formatDate(item.expires_at)}${item.is_expired ? " (expiré)" : ""}` : "Jamais — accès permanent"}
            />
            {isFile && (
              <DetailRow
                label="Téléchargements restants"
                value={item.download_limit > 0 ? `${remaining} / ${item.download_limit}` : "Illimité"}
              />
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-line bg-white p-4 sm:p-6" data-testid="launch-support-card">
          <h2 className="flex items-center gap-2 font-display text-base font-extrabold text-ink">
            <HelpCircle className="h-4 w-4 text-brand" /> Besoin d'aide ?
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            Si vous rencontrez un problème lors du téléchargement ou si le fichier est endommagé, contactez notre support.
          </p>
          <div className="mt-4 space-y-2.5">
            {[
              { icon: AlertCircle, label: "Fichier corrompu" },
              { icon: WifiOff, label: "Téléchargement interrompu" },
              { icon: Gauge, label: "Vitesse de téléchargement lente" },
              { icon: HelpCircle, label: "Autre problème" },
            ].map(({ icon: Icon, label }) => (
              <p key={label} className="flex items-center gap-2 text-xs font-semibold text-slate-600">
                <Icon className="h-3.5 w-3.5 text-slate-400" /> {label}
              </p>
            ))}
          </div>
          <Link
            to="/support"
            data-testid="launch-contact-support-btn"
            className="mt-5 flex items-center justify-center gap-2 rounded-full border border-brand/30 px-5 py-2.5 text-xs font-bold text-brand transition-colors duration-200 hover:bg-brand hover:text-white"
          >
            <Headphones className="h-3.5 w-3.5" /> Contacter le support
          </Link>
        </div>
      </div>

      {/* ---- Retour ---- */}
      <div className="pt-2 text-center">
        <Link to={backUrl} data-testid="launch-back-btn" className="inline-flex items-center gap-2 text-sm font-bold text-brand hover:underline">
          <MoveLeft className="h-4 w-4" /> Retour à mes téléchargements
        </Link>
      </div>
    </div>
  );
}
